import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Bug,
  Lightbulb,
  Plus,
  Check,
  Trash2,
  Loader2,
  CircleDot,
  MessageSquare,
} from "lucide-react";

const TYPE_META = {
  bug: {
    label: "Fehlermeldung",
    icon: Bug,
    color: "text-rose-600",
    ring: "border-rose-200 bg-rose-50",
  },
  improvement: {
    label: "Verbesserung",
    icon: Lightbulb,
    color: "text-kauz-gold-dark",
    ring: "border-amber-200 bg-amber-50",
  },
};

const STATUS_META = {
  open: { label: "Offen", cls: "bg-slate-100 text-slate-700 border-slate-200", icon: CircleDot },
  in_progress: { label: "In Bearbeitung", cls: "bg-blue-100 text-blue-800 border-blue-200", icon: Loader2 },
  done: { label: "Erledigt", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Check },
  rejected: { label: "Abgelehnt", cls: "bg-slate-100 text-slate-500 border-slate-200 line-through", icon: Trash2 },
};

export default function Feedback() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/feedback");
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Einträge nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const g = { open: [], in_progress: [], done: [], rejected: [] };
    items.forEach((it) => (g[it.status] || g.open).push(it));
    return g;
  }, [items]);

  const filtered = tab === "all" ? items : grouped[tab] || [];

  const counts = {
    open: grouped.open.length,
    in_progress: grouped.in_progress.length,
    done: grouped.done.length,
    all: items.length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="ph-label">Rückmeldungen</p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">
            Fehler &amp; Verbesserungen
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin
              ? "Alle Meldungen deines Teams – abhaken, kommentieren, umsetzen."
              : "Melde Fehler oder schlage Verbesserungen vor. Der Chef sieht deine Rückmeldung."}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-feedback" className="bg-kauz-navy hover:bg-kauz-navy-dark h-10">
              <Plus className="h-4 w-4 mr-1.5" /> Neue Rückmeldung
            </Button>
          </DialogTrigger>
          <NewFeedbackDialog
            onCreated={() => {
              setDialogOpen(false);
              load();
            }}
          />
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="open" data-testid="tab-fb-open">Offen ({counts.open})</TabsTrigger>
          <TabsTrigger value="in_progress" data-testid="tab-fb-progress">In Bearbeitung ({counts.in_progress})</TabsTrigger>
          <TabsTrigger value="done" data-testid="tab-fb-done">Erledigt ({counts.done})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-fb-all">Alle ({counts.all})</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="text-slate-400 text-sm py-10 text-center">Lade…</div>
          ) : filtered.length === 0 ? (
            <div className="ph-card p-8 text-center text-slate-400">
              <MessageSquare className="h-8 w-8 mx-auto text-slate-300" />
              <p className="mt-2 text-sm">Keine Einträge in dieser Kategorie.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((it) => (
                <FeedbackCard
                  key={it.id}
                  item={it}
                  isAdmin={isAdmin}
                  currentUserId={user.id}
                  onChanged={load}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FeedbackCard({ item, isAdmin, currentUserId, onChanged }) {
  const tMeta = TYPE_META[item.type] || TYPE_META.improvement;
  const sMeta = STATUS_META[item.status] || STATUS_META.open;
  const TIcon = tMeta.icon;
  const SIcon = sMeta.icon;
  const [busy, setBusy] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState(item.admin_comment || "");

  const canDelete = isAdmin || (item.user_id === currentUserId && item.status === "open");

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/feedback/${item.id}`, { status });
      toast.success("Status aktualisiert");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const saveComment = async () => {
    setBusy(true);
    try {
      await api.patch(`/feedback/${item.id}`, { admin_comment: comment });
      toast.success("Kommentar gespeichert");
      setShowComment(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Wirklich löschen?")) return;
    setBusy(true);
    try {
      await api.delete(`/feedback/${item.id}`);
      toast.success("Gelöscht");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`feedback-card-${item.id}`}
      className={`ph-card p-4 md:p-5 border-l-[4px] ${
        item.type === "bug" ? "border-l-rose-500" : "border-l-kauz-gold"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-lg grid place-items-center border ${tMeta.ring}`}>
          <TIcon className={`h-5 w-5 ${tMeta.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${tMeta.color}`}>
              {tMeta.label}
            </span>
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${sMeta.cls} inline-flex items-center gap-1`}>
              <SIcon className={`h-3 w-3 ${item.status === "in_progress" ? "animate-spin" : ""}`} />
              {sMeta.label}
            </span>
          </div>
          <h3 className="font-display text-base font-semibold text-slate-900 mt-1">{item.title}</h3>
          {item.description && (
            <p className="text-sm text-slate-600 whitespace-pre-line mt-1.5">{item.description}</p>
          )}
          <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-3 flex-wrap">
            <span>
              von <span className="text-slate-600 font-medium">{item.user_name || "—"}</span>
            </span>
            <span>· {new Date(item.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
            {item.resolved_by_name && (
              <span>· {sMeta.label} durch {item.resolved_by_name}</span>
            )}
          </div>

          {item.admin_comment && !showComment && (
            <div className="mt-3 border border-slate-200 rounded-md bg-slate-50 p-2.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                Notiz vom Chef
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-line">{item.admin_comment}</div>
            </div>
          )}

          {isAdmin && showComment && (
            <div className="mt-3">
              <Textarea
                data-testid={`feedback-comment-${item.id}`}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Interne Notiz für den Mitarbeiter …"
                rows={3}
                className="text-sm"
              />
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={saveComment} disabled={busy} className="bg-kauz-navy hover:bg-kauz-navy-dark h-8">
                  Speichern
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => setShowComment(false)}>
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {(isAdmin || canDelete) && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2 justify-end">
          {isAdmin && item.status !== "in_progress" && (
            <Button size="sm" variant="outline" className="h-8" disabled={busy}
              onClick={() => setStatus("in_progress")} data-testid={`fb-progress-${item.id}`}>
              <Loader2 className="h-3.5 w-3.5 mr-1" /> In Bearbeitung
            </Button>
          )}
          {isAdmin && item.status !== "done" && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" disabled={busy}
              onClick={() => setStatus("done")} data-testid={`fb-done-${item.id}`}>
              <Check className="h-3.5 w-3.5 mr-1" /> Erledigt
            </Button>
          )}
          {isAdmin && item.status !== "rejected" && item.status !== "done" && (
            <Button size="sm" variant="outline" className="h-8 text-slate-500" disabled={busy}
              onClick={() => setStatus("rejected")} data-testid={`fb-reject-${item.id}`}>
              Ablehnen
            </Button>
          )}
          {isAdmin && (item.status === "done" || item.status === "rejected") && (
            <Button size="sm" variant="outline" className="h-8" disabled={busy}
              onClick={() => setStatus("open")} data-testid={`fb-reopen-${item.id}`}>
              Wieder öffnen
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" variant="outline" className="h-8" onClick={() => setShowComment((v) => !v)} data-testid={`fb-comment-${item.id}`}>
              {item.admin_comment ? "Notiz bearbeiten" : "Notiz hinzufügen"}
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="outline" className="h-8 text-rose-700 border-rose-200 hover:bg-rose-50"
              onClick={remove} data-testid={`fb-delete-${item.id}`} disabled={busy}>
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Löschen
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function NewFeedbackDialog({ onCreated }) {
  const [type, setType] = useState("improvement");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Bitte einen Titel eingeben");
      return;
    }
    setBusy(true);
    try {
      await api.post("/feedback", { type, title: title.trim(), description: description.trim() });
      toast.success("Danke! Rückmeldung übermittelt.");
      setTitle("");
      setDescription("");
      setType("improvement");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">Neue Rückmeldung</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label className="ph-label">Art</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="feedback-type-select" className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="improvement">
                <span className="inline-flex items-center gap-2"><Lightbulb className="h-4 w-4 text-kauz-gold-dark" /> Verbesserungsvorschlag</span>
              </SelectItem>
              <SelectItem value="bug">
                <span className="inline-flex items-center gap-2"><Bug className="h-4 w-4 text-rose-600" /> Fehlermeldung</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="ph-label">Titel</Label>
          <Input
            data-testid="feedback-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Kurz &amp; knapp – z. B. „Urlaubs-PDF lädt langsam"
            maxLength={200}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label className="ph-label">Beschreibung (optional)</Label>
          <Textarea
            data-testid="feedback-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Was ist passiert / was schlägst du vor?"
            rows={5}
            maxLength={4000}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="submit"
            data-testid="feedback-submit-button"
            disabled={busy}
            className="bg-kauz-navy hover:bg-kauz-navy-dark"
          >
            {busy ? "Sende…" : "Absenden"}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}

// avoid unused import warning
void Badge;
